// MapPopupsStore.ts
import { makeAutoObservable, runInAction } from "mobx";

type PopupItem = { id: string; lng: number; lat: number };
type VisiblePopup = (PopupItem & { mode: "pinned" }) | (PopupItem & { mode: "hover" });

export type ClickPopup = { kind: "rain"; lng: number; lat: number };

export type HoverPopup = { kind: "connection"; id: Dryp.NetworkConnectionId; lng: number; lat: number };

export class MapPopupsStore {
	hover: { id: string; lng: number; lat: number } | null = null;
	pinned = new Map<string, { lng: number; lat: number }>();
	clickPopup: ClickPopup | null = null;
	hoverPopup: HoverPopup | null = null;

	private hoverTid: number | null = null;
	private hoverLock = false;

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true });
	}

	openClickPopup(popup: ClickPopup) {
		this.clickPopup = popup;
	}

	closeClickPopup() {
		this.clickPopup = null;
	}

	openHoverPopup(popup: HoverPopup) {
		this.hoverPopup = popup;
	}

	closeHoverPopup() {
		this.hoverPopup = null;
	}

	private cancelHoverClear() {
		if (this.hoverTid != null) {
			clearTimeout(this.hoverTid);
			this.hoverTid = null;
		}
	}

	keepHover() {
		this.cancelHoverClear();
	}
	lockHover() {
		this.cancelHoverClear();
		this.hoverLock = true;
	}

	unlockHover(delayMs = 160) {
		this.hoverLock = false;
		this.scheduleHoverClear(delayMs);
	}

	scheduleHoverClear(ms = 160) {
		if (this.hoverLock) return;
		this.cancelHoverClear();
		this.hoverTid = window.setTimeout(() => {
			// ✅ mutate inside an action
			runInAction(() => {
				this.hover = null;
				this.hoverTid = null;
			});
		}, ms);
	}

	setHover(id: string, lng: number, lat: number) {
		const sid = String(id);
		if (this.pinned.has(sid)) {
			this.clearHover();
			return;
		}
		this.keepHover();
		this.hover = { id: sid, lng, lat };
	}

	moveHover(lng: number, lat: number) {
		if (!this.hover) return;
		this.hover = { ...this.hover, lng, lat };
	}

	clearHover() {
		this.cancelHoverClear();
		this.hover = null;
	}

	togglePin(id: string, lng: number, lat: number) {
		if (this.pinned.has(id)) this.pinned.delete(id);
		else this.pinned.set(id, { lng, lat });
	}

	unpin(id: string) {
		this.pinned.delete(id);
	}

	prunePinned(validIds: Set<string>) {
		for (const id of Array.from(this.pinned.keys())) {
			if (!validIds.has(String(id))) this.pinned.delete(id);
		}
	}

	ensureHoverIsValid(validIds: Set<string>) {
		if (this.hover && !validIds.has(this.hover.id)) this.clearHover();
	}

	get pinnedList() {
		return Array.from(this.pinned, ([id, pos]) => ({ id, ...pos }));
	}

	get visible(): VisiblePopup[] {
		const pinnedPopups: VisiblePopup[] = this.pinnedList.map(p => ({ mode: "pinned", ...p }));
		const shouldShowHover = this.hover && !this.pinned.has(this.hover.id);
		if (shouldShowHover) pinnedPopups.push({ mode: "hover", ...this.hover! });
		return pinnedPopups;
	}
}
